// src/app/interceptors/auth.interceptor.ts
import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap, filter, take, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly REFRESH_AHEAD_MS = 60_000;

  constructor(private authService: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getAccessToken();
    const expiresAt = this.authService.getAccessTokenExpiry();

    if (token && expiresAt && Date.now() >= expiresAt - this.REFRESH_AHEAD_MS) {
      return this.authService.forceRefresh().pipe(
        switchMap((newToken: string) => {
          const access = newToken || this.authService.getAccessToken();
          const authReq = access ? this.addTokenHeader(req, access) : req;
          return next.handle(authReq);
        }),
        catchError((err) => {
          return throwError(() => err);
        })
      );
    }

    const authReq = token ? this.addTokenHeader(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.authService.forceRefresh().pipe(
            switchMap((newToken: string) => {
              const access = newToken || this.authService.getAccessToken();
              if (!access) {
                return throwError(() => error);
              }
              const retryReq = this.addTokenHeader(req, access);
              return next.handle(retryReq);
            }),
            catchError((refreshErr) => {
              this.authService.clearTokens();
              return throwError(() => refreshErr);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  private addTokenHeader(req: HttpRequest<any>, token: string) {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
